// src/utils/preload-cta-videos.ts
//
// Preloads all CTA video files into the browser's media cache using hidden
// <video preload="auto"> elements. This is the only reliable way to populate
// the media cache on Android WebView (fetch() uses a separate HTTP cache).
//
// Call `startCtaPreload()` as early as possible (e.g. when authed becomes true).
// It resolves once every video fires `canplaythrough`, or after a timeout.
// The hidden elements stay in the DOM to prevent Android WebView from evicting
// them from the media cache — identical to the technique in cta.hooks.ts.

const CTA_VIDEOS = [
  '/cta/rest-loop.mp4',
  '/cta/rest-to-recording.mp4',
  '/cta/recording-loop.mp4',
  '/cta/pause-on.mp4',
  '/cta/pause-off.mp4',
  '/cta/stop-recording-from-recording.mp4',
  '/cta/stop-recording-from-pause.mp4',
];

const PRELOAD_TIMEOUT_MS = 8000;

let preloadPromise: Promise<void> | null = null;

/**
 * Start preloading CTA videos. Safe to call multiple times — only runs once.
 * Resolves when all videos are cached or the timeout fires.
 */
export function startCtaPreload(): Promise<void> {
  if (preloadPromise) return preloadPromise;

  preloadPromise = new Promise<void>((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };

    const timeout = setTimeout(done, PRELOAD_TIMEOUT_MS);

    let loaded = 0;
    for (const src of CTA_VIDEOS) {
      const v = document.createElement('video');
      v.preload = 'auto';
      v.muted = true;
      v.playsInline = true;
      v.style.position = 'fixed';
      v.style.width = '1px';
      v.style.height = '1px';
      v.style.opacity = '0';
      v.style.pointerEvents = 'none';
      v.style.zIndex = '-1';
      v.addEventListener('canplaythrough', () => {
        loaded++;
        if (loaded >= CTA_VIDEOS.length) {
          clearTimeout(timeout);
          done();
        }
      }, { once: true });
      v.addEventListener('error', () => {
        loaded++;
        if (loaded >= CTA_VIDEOS.length) {
          clearTimeout(timeout);
          done();
        }
      }, { once: true });
      v.src = src;
      v.load();
      // Keep in DOM to prevent cache eviction on Android WebView
      document.body.appendChild(v);
    }
  });

  return preloadPromise;
}
